/**
 * MagicFurnish shape system — single source of truth for corner radii and
 * control heights. Engineers should NEVER write a raw radius / height
 * literal in a component; import the right token below.
 *
 * Two shape families:
 *   1. CAPSULES  — full-pill (9999px). Buttons, chips, menu rows.
 *   2. SHELLS    — finite radius for floating containers. Modals, menus,
 *                  flyouts, editor toolbar.
 *
 * Nesting rule (non-negotiable):
 *   Inner radius + shellInset ≤ outer radius.
 *
 * Example: 28px shell with 6px inset → an inner field (14px) fits
 * (14 + 6 = 20 ≤ 28); an inner section block (18px) fits (18 + 6 = 24 ≤ 28).
 * That's why login feels right (42 shell → 14 field → pill CTA), and it's
 * why this system standardizes on a small, intentional ladder of radii.
 *
 * Heights are limited to three values across the app. Mixing 44/48/50 is
 * intentional — they map to row / field / CTA tiers, not random.
 */

// ─── CAPSULES ─────────────────────────────────────────────────────────────

/** All capsules — primary CTAs, secondary CTAs, chips, menu row hovers. */
export const shapePill = "9999px";

// ─── SHELLS ───────────────────────────────────────────────────────────────

/**
 * Universal shell for floating UI: centered modals, overflow menus,
 * inquire / share / auth dialogs, listing / account / user-home form
 * dialogs. One value so the app reads as one product.
 */
export const shapeShell = "28px";

/**
 * Editor floating flyouts (filter panel, zone dropdown). Visually
 * "lighter" than full modals so the floor plan stays the primary
 * surface. Sits above 18px toolbar pills, so 16px keeps the nesting
 * honest (16 < 18 < 28).
 */
export const shapeFlyout = "16px";

/**
 * Editor top bar pill (undo/redo, Done) + bottom toolbar pill (Add Items,
 * Shuffle, Save/Share, Inquire). Specific editor chrome that has lived at
 * 16–18px since launch.
 */
export const shapeToolbarPill = "18px";

/**
 * Auth page card — full-bleed marketing tier (/login, /signup, /forgetPsw).
 * Intentionally rounder than dialog shells; pairs with the auth photo
 * background and is the only surface that lives outside a Dialog wrapper.
 */
export const shapeAuthCard = "42px";

// ─── INSET CHILDREN ───────────────────────────────────────────────────────

/** Text inputs inside any shell (auth, modals, listing forms). */
export const shapeField = "14px";

/** Nested glass blocks inside form-heavy dialogs (listing sections). */
export const shapeSection = "18px";

/**
 * Gutter between shell edge and inset children, in MUI spacing units
 * (1 = 8px). Matches ACTION_MENU_GUTTER. Used by the nesting rule above.
 */
export const shellInset = 0.75; // 6px at default theme spacing

// ─── CONTROL HEIGHTS ──────────────────────────────────────────────────────

/**
 * Menu rows, toggle rows, header icon buttons, tappable links.
 * iOS minimum touch target. Use for any pill that lives inside a shell.
 */
export const heightRow = 44;

/** All text inputs across auth, modals, analytics listing forms. */
export const heightField = 48;

/** All primary / secondary / destructive pill CTAs. */
export const heightCta = 50;
